import { type CreateInboxInvoiceDto, DocumentTypeEnum } from "@finway-group/shared/lib/models"
import { InboxInvoice } from "@finway-group/shared/lib/models/invoice/inboxInvoice.model"
import { type Employee } from "@finway-group/shared/lib/models/user/employee.model"
import axios from "axios"
import { type TFunction } from "i18next"
import type { InboxDocument } from "~Shared/models/inboxDocument"
import { type QueryParams } from "~Shared/queries/consts"
import { NotificationService } from "~Shared/services"
import { DialogService } from "~Shared/services/dialog.service"
import { NotificationDurationSec, NotificationTypeEnum } from "~Shared/services/notification.service"
import { convertUnencodedParamStringToObject } from "../config/axios.utils"

const MODEL = "inboxinvoices"

const deserializeInboxDocument = (doc: InboxDocument<string>): InboxDocument<string> => {
    if (doc.documentType === DocumentTypeEnum.INVOICE) {
        return new InboxInvoice<string>(doc)
    }
    return doc
}

export const InboxInvoiceService = {
    updateOne: async (id: string, updatedInboxInvoice: InboxDocument) => {
        const { data } = await axios.put(`/${MODEL}/${id}`, updatedInboxInvoice)
        return deserializeInboxDocument(data)
    },
    moveInboxInvoice: async (id: string, companyId: string) => {
        const { data } = await axios.patch(`/${MODEL}/${id}/move`, { companyId })
        return deserializeInboxDocument(data)
    },
    fetchInboxInvoicesCount: async (query = "") => {
        const {
            data: { count },
        } = await axios.get(`/${MODEL}/reports/counts`, { params: convertUnencodedParamStringToObject(query) })
        return count
    },

    fetchAllInboxInvoices: async (queryParams: QueryParams) => {
        const defaultParams: QueryParams = {
            page: 1,
            limit: 1000,
        }
        const params: QueryParams = { ...defaultParams, ...queryParams }
        const {
            data: { docs, totalPages, totalDocs },
        } = await axios.get(`/${MODEL}`, { params })

        return {
            inboxInvoices: docs.map(deserializeInboxDocument),
            totalDocs,
            totalPages,
        }
    },
    fetchInboxInvoices: async (queryParams: any, source: any, timeout?: number) => {
        const params = convertUnencodedParamStringToObject(
            `page=${queryParams.page}&limit=${queryParams.limit}${queryParams.filterQueryString}${queryParams.searchQueryString}${queryParams.sortQueryString}`,
        )
        const { data } = await axios.get(`/${MODEL}`, {
            cancelToken: source.token,
            timeout,
            params,
        })
        const { docs } = data
        return {
            ...data,
            docs: docs.map(deserializeInboxDocument),
        }
    },
    fetchOneInboxInvoice: async (id: string) => {
        const { data } = await axios.get<InboxDocument>(`/${MODEL}/${id}`)
        return deserializeInboxDocument(data)
    },
    retryInboxInvoiceOcr: async (id: string) => {
        await axios.get(`/${MODEL}/retryOcr/${id}`)
    },
    changeInboxInvoiceDocumentType: async (id: string, newDocumentType: DocumentTypeEnum) => {
        await axios.patch(`/${MODEL}/${id}/document-type`, { newDocumentType })
    },
    createInboxInvoice: async (createInboxInvoice: CreateInboxInvoiceDto) => {
        const { data } = await axios.post<InboxDocument>(`/${MODEL}`, createInboxInvoice)
        return deserializeInboxDocument(data)
    },
    discardInboxInvoice: async (id: string) => axios.delete(`/${MODEL}/${id}`),
    deleteManyInboxInvoices: async (ids: Array<string>) => axios.post(`/${MODEL}/delete-inbox-invoices`, { ids }),
    fetchMultipleInvoicesByIds: async (ids: Array<string>) => {
        const invoicePromises = ids.map((invoiceId) => InboxInvoiceService.fetchOneInboxInvoice(invoiceId))
        const invoices = await Promise.all(invoicePromises)

        return invoices
    },

    assignInvoiceToUser: async (inboxInvoice: InboxDocument, employee: Employee, t: TFunction) => {
        if (!(await DialogService.confirmAssignInvoiceToUser(employee.getFullName()))) {
            return
        }
        const newInvoice = structuredClone(inboxInvoice)
        newInvoice.assignedTo = employee.id
        try {
            const data = await InboxInvoiceService.updateOne(inboxInvoice._id, newInvoice)
            NotificationService.send(
                NotificationTypeEnum.SUCCESS,
                t("notification:invoice_inbox.assign.title"),
                t("notification:invoice_inbox.assign.message", { name: data.fileName, employee: employee.getFullName() }),
                NotificationDurationSec.DEFAULT,
            )
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:invoice_inbox.assign.title"))
        }
    },
}
